"use client"

import React, { useState } from "react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../shadcn/dropdown-menu"

/**
 * 드롭다운 메뉴
 *
 * @description
 * 클릭으로 열리는 드롭다운 메뉴입니다. 두 가지 API를 지원합니다:
 *
 * **1. trigger + children API (권장)** — 평탄한 JSX로 에이전트 친화적
 * 메뉴 아이템 클릭 시 자동으로 메뉴가 닫힙니다.
 *
 * **2. renderButton + renderMenu API (기존)** — onClose를 수동 호출
 *
 * @example
 * ```tsx
 * // 1. trigger + children API (권장)
 * <TMenu trigger={<TIconButton name="more_vert" color="ghost" />}>
 *   <TMenuItem label="편집" onClick={handleEdit} />
 *   <TMenuItem label="삭제" destructive onClick={handleDelete} />
 * </TMenu>
 *
 * // 2. renderButton + renderMenu API (기존)
 * <TMenu
 *   renderButton={({ open }) => (
 *     <TIconButton name="more_vert" color="ghost" selected={open} />
 *   )}
 *   renderMenu={({ onClose }) => (
 *     <>
 *       <TMenuItem label="편집" onClick={() => { handleEdit(); onClose() }} />
 *       <TMenuItem label="삭제" destructive onClick={() => { handleDelete(); onClose() }} />
 *     </>
 *   )}
 * />
 * ```
 */
/**
 * trigger + children API: 평탄한 JSX로 에이전트 친화적. 아이템 클릭 시 자동 닫힘
 */
interface TMenuTriggerProps {
	/**
	 * 메뉴 트리거 요소 (trigger + children API용)
	 * trigger가 있으면 children을 메뉴 내용으로 사용하며, 아이템 클릭 시 자동으로 닫힘
	 */
	trigger: React.ReactNode
	/**
	 * 메뉴 내용 (trigger API용) — TMenuItem 등을 직접 배치
	 * 각 아이템의 onClick 후 자동으로 메뉴가 닫힘
	 */
	children?: React.ReactNode
	renderButton?: never
	renderMenu?: never
}

/**
 * renderButton + renderMenu API: onClose를 수동 호출하는 기존 패턴
 */
interface TMenuRenderProps {
	trigger?: never
	children?: never
	/**
	 * 메뉴 트리거 버튼 렌더링 (renderButton + renderMenu API용)
	 * @param props.open 메뉴 열림 상태 (selected 등에 활용)
	 */
	renderButton: (props: { open?: boolean }) => React.ReactNode
	/**
	 * 메뉴 내용 렌더링 (renderButton + renderMenu API용)
	 * @param props.onClose 메뉴 닫기 함수 — 메뉴 아이템 onClick 안에서 반드시 호출
	 */
	renderMenu: (props: { onClose: () => void }) => React.ReactNode
}

interface TMenuBaseProps {
	/** 제어 모드용 열림 상태 */
	open?: boolean
	/** 메뉴 정렬 @default "start" */
	align?: "start" | "center" | "end"
	/** 메뉴 방향 @default "bottom" */
	side?: "top" | "right" | "bottom" | "left"
	/** 정렬 오프셋 @default 0 */
	alignOffset?: number
	/** 방향 오프셋 @default 4 */
	sideOffset?: number
	/** 추가 CSS 클래스 */
	className?: string
	/** 메뉴 닫힐 때 콜백 */
	onMenuClose?: () => void
	/** 메뉴 열릴 때 콜백 */
	onMenuOpen?: () => void
	/**
	 * 모달 모드 (배경 스크롤 잠금 여부)
	 * - true: 메뉴 열림 시 배경 스크롤 차단 (기본)
	 * - false: 메뉴 열림 시에도 배경 스크롤 허용, 스크롤 시 메뉴 자동 닫힘
	 * @default true
	 */
	modal?: boolean
}

type TMenuProps = TMenuBaseProps & (TMenuTriggerProps | TMenuRenderProps)

export const TMenu = React.memo((props: TMenuProps) => {
	const {
		trigger,
		children,
		renderButton,
		renderMenu,
		open,
		align = "start",
		side = "bottom",
		alignOffset = 0,
		sideOffset = 4,
		className,
		onMenuClose,
		onMenuOpen,
		modal = true
	} = props

	const [isOpen, setIsOpen] = useState(false)

	// modal=false 시 스크롤/터치 이벤트로 메뉴 자동 닫기
	const onMenuCloseRef = React.useRef(onMenuClose)
	onMenuCloseRef.current = onMenuClose

	React.useEffect(() => {
		if (modal || !(open ?? isOpen)) return

		const handleDismiss = () => {
			if (open === undefined) {
				setIsOpen(false)
			}
			onMenuCloseRef.current?.()
		}

		const captureOpts = { capture: true } as const
		window.addEventListener("scroll", handleDismiss, captureOpts)
		const touchOpts = { capture: true, passive: true } as const
		window.addEventListener("touchstart", handleDismiss, touchOpts)
		return () => {
			window.removeEventListener("scroll", handleDismiss, captureOpts)
			window.removeEventListener("touchstart", handleDismiss, touchOpts)
		}
	}, [modal, open, isOpen])

	const handleClose = React.useCallback(() => {
		setIsOpen(false)
		onMenuClose?.()
	}, [onMenuClose])

	const onOpenChange = React.useCallback(
		(open: boolean) => {
			if (open) {
				setIsOpen(true)
				onMenuOpen?.()
			} else {
				setIsOpen(false)
				onMenuClose?.()
			}
		},
		[onMenuOpen, onMenuClose]
	)

	const handleEscapeKeyDown = React.useCallback(
		(e: KeyboardEvent) => {
			e.preventDefault()
			setIsOpen(false)
			onMenuClose?.()
		},
		[onMenuClose]
	)

	// trigger + children API: 아이템 클릭 시 자동으로 메뉴 닫힘
	const useTriggerAPI = trigger !== undefined

	if (process.env.NODE_ENV === "development") {
		if (useTriggerAPI && (renderButton || renderMenu)) {
			console.warn("TMenu: trigger와 renderButton/renderMenu를 동시에 사용할 수 없습니다. trigger API가 우선 적용됩니다.")
		}
	}

	const wrappedChildren = React.useMemo(() => {
		if (!children) return null

		return React.Children.map(children, (child) => {
			if (!React.isValidElement<{ onClick?: (event: React.MouseEvent) => void }>(child)) return child

			if (process.env.NODE_ENV === "development" && child.type === React.Fragment) {
				console.warn("TMenu: Fragment를 직접 자식으로 사용하면 자동 닫기가 동작하지 않습니다. 직계 TMenuItem만 자동 닫기를 지원합니다.")
			}

			const originalOnClick = child.props.onClick

			return React.cloneElement(child, {
				onClick: (event: React.MouseEvent) => {
					originalOnClick?.(event)
					handleClose()
				}
			})
		})
	}, [children, handleClose])

	const triggerContent = useTriggerAPI ? trigger : renderButton?.({ open: open ?? isOpen })
	const menuContent = useTriggerAPI ? wrappedChildren : renderMenu?.({ onClose: handleClose })

	return (
		<DropdownMenu open={open ?? isOpen} onOpenChange={onOpenChange} modal={modal}>
			<DropdownMenuTrigger
				asChild
				onPointerDown={(e: React.PointerEvent) => {
					// 모든 natural pointerdown을 차단하고, onClickCapture에서 마커(detail:1738) 붙인 synthetic pointerdown으로 재발송
					// 터치 디바이스에서 pointerdown(스크롤)과 click(탭) 충돌 방지
					// ref: https://github.com/radix-ui/primitives/issues/1912#issuecomment-2505498964
					if ((e.nativeEvent as PointerEvent & { detail: number }).detail !== 1738) {
						e.preventDefault()
					}
				}}
			>
				<div
					onClickCapture={(e: React.MouseEvent) => {
						// capture phase: 자식의 stopPropagation()보다 먼저 synthetic pointerdown 발송
						e.currentTarget.dispatchEvent(
							new PointerEvent("pointerdown", {
								bubbles: true,
								cancelable: true,
								detail: 1738
							})
						)
					}}
					onClick={(e: React.MouseEvent) => {
						// bubble phase: 부모(카드 등)로의 click 전파 차단
						e.stopPropagation()
					}}
				>
					{triggerContent}
				</div>
			</DropdownMenuTrigger>
			<DropdownMenuContent align={align} side={side} alignOffset={alignOffset} sideOffset={sideOffset} className={className} onEscapeKeyDown={handleEscapeKeyDown}>
				{menuContent}
			</DropdownMenuContent>
		</DropdownMenu>
	)
})

TMenu.displayName = "TMenu"
